import { useEffect, useState, type FormEvent } from "react";
import { makeGetApiCall, makePostApiCall } from "../../../shared/makeApiCall";
import { receivingApi } from "../../../shared/receivingApi";
import SelectMenu from "../../controls/SelectMenu";
import InputNumberOnly from "../../controls/InputNumberOnly";
import EmptyModal from "../../modals/EmptyModal";
import type {
  InventoryLocation,
  ProductInventoryResponse,
  WarehouseOption,
} from "../../../types/inventory";
import type { SelectOption } from "../../../types/api";

const quantityText = (value: number) =>
  Number(value).toLocaleString(undefined, { maximumFractionDigits: 4 });

interface SelectMenuEvent {
  target: { name?: string; value: SelectOption<number | string> | null };
}

interface NumberInputEvent {
  target: { name?: string; value: number | string };
}

interface EmptyModalState {
  open?: boolean;
}

interface ProductInventoryProps {
  productId: string | number;
}

export default function ProductInventory({ productId }: ProductInventoryProps) {
  const [stock, setStock] = useState(0);
  const [locations, setLocations] = useState<InventoryLocation[]>([]);
  const [warehouses, setWarehouses] = useState<WarehouseOption[]>([]);
  const [destLocations, setDestLocations] = useState<WarehouseOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [moveOpen, setMoveOpen] = useState(false);
  const [fromLocationId, setFromLocationId] = useState("");
  const [toWarehouseId, setToWarehouseId] = useState("");
  const [toLocationId, setToLocationId] = useState("");
  const [quantity, setQuantity] = useState<number | string | null>(null);

  const selectedOption = <T extends SelectOption<number | string>>(options: T[], value: string | number) =>
    options.find((option) => Number(option.value) === Number(value)) || null;

  const fromOptions = locations.map((row) => ({
    value: row.warehouse_location_id,
    label: `${row.warehouse_name} · ${row.location_name} (${quantityText(row.quantity)})`,
  }));

  const sourceRow = locations.find(
    (row) => Number(row.warehouse_location_id) === Number(fromLocationId)
  );
  const available = sourceRow ? Number(sourceRow.quantity) : 0;

  const load = async () => {
    setLoading(true);
    setError("");
    const result = await makeGetApiCall<ProductInventoryResponse>(`/products/${productId}/inventory`);
    if (result.success) {
      setStock(result.data.stock ?? 0);
      setLocations(result.data.locations || []);
    } else {
      setError(result.data?.message || "Could not load inventory.");
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
    receivingApi.warehouseOptions().then((result) => {
      if (result.success) setWarehouses(result.data || []);
    });
  }, [productId]);

  const selectDestWarehouse = async (warehouseId: string | number) => {
    setToWarehouseId(String(warehouseId || ""));
    setToLocationId("");
    setDestLocations([]);
    if (!warehouseId) return;
    const result = await receivingApi.locations(warehouseId);
    if (result.success) {
      setDestLocations(result.data || []);
    }
  };

  const openMove = (locationId?: number) => {
    setFromLocationId(locationId ? String(locationId) : "");
    setToWarehouseId("");
    setToLocationId("");
    setDestLocations([]);
    setQuantity(null);
    setError("");
    setMoveOpen(true);
  };

  const closeMove = () => {
    setMoveOpen(false);
    setError("");
  };

  const handleMoveModal = (next: EmptyModalState) => {
    if (!next?.open) closeMove();
  };

  const submitMove = async (event: FormEvent) => {
    event.preventDefault();
    setSaving(true);
    setError("");
    const result = await makePostApiCall<ProductInventoryResponse>(`/products/${productId}/inventory-transfers`, {
      from_warehouse_location_id: Number(fromLocationId),
      to_warehouse_location_id: Number(toLocationId),
      quantity,
    });
    if (result.success) {
      setStock(result.data.stock ?? 0);
      setLocations(result.data.locations || []);
      closeMove();
    } else {
      setError(
        result.data?.message ||
          Object.values(result.data?.errors || {}).flat()[0] ||
          "Could not move inventory."
      );
    }
    setSaving(false);
  };

  const moveForm = (
    <form onSubmit={submitMove} className="space-y-4">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Move inventory</h2>
          <p className="text-sm text-gray-500">
            Move quantity from one warehouse location to another. Product stock does not change.
          </p>
        </div>
        <button
          type="button"
          onClick={closeMove}
          className="text-sm font-semibold text-gray-500 hover:text-gray-700"
        >
          Close
        </button>
      </div>
      {error && (
        <p className="rounded-lg bg-red-50 p-3 text-sm font-medium text-red-700">{error}</p>
      )}
      <div className="grid gap-4 md:grid-cols-2">
        <SelectMenu
          parentClass="md:col-span-2"
          title="From location"
          name="from_warehouse_location_id"
          options={fromOptions}
          value={selectedOption(fromOptions, fromLocationId)}
          handleInputChange={(event: SelectMenuEvent) => {
            setFromLocationId(String(event.target.value?.value ?? ""));
            setQuantity(null);
          }}
          placeholder="Select source location"
        />
        <SelectMenu
          title="To warehouse"
          name="to_warehouse_id"
          options={warehouses}
          value={selectedOption(warehouses, toWarehouseId)}
          handleInputChange={(event: SelectMenuEvent) =>
            selectDestWarehouse(event.target.value?.value ?? "")
          }
          placeholder="Select warehouse"
        />
        <SelectMenu
          title="To location"
          name="to_warehouse_location_id"
          options={destLocations.filter(
            (option) => Number(option.value) !== Number(fromLocationId)
          )}
          value={selectedOption(destLocations, toLocationId)}
          handleInputChange={(event: SelectMenuEvent) =>
            setToLocationId(String(event.target.value?.value ?? ""))
          }
          placeholder="Select location"
        />
        <InputNumberOnly
          label={available > 0 ? `Quantity (up to ${quantityText(available)})` : "Quantity"}
          name="quantity"
          labelOn={true}
          value={quantity === "" || quantity == null ? null : Number(quantity)}
          onChange={(event: NumberInputEvent) => setQuantity(event.target.value)}
          className="w-full"
          min={0.0001}
          max={available || undefined}
          minFractionDigits={0}
          maxFractionDigits={4}
        />
      </div>
      <div className="flex justify-end">
        <button
          disabled={saving || !fromLocationId || !toLocationId || !(Number(quantity) > 0)}
          className="rounded-lg bg-primary-600 px-4 py-2 font-semibold text-white disabled:opacity-50"
        >
          {saving ? "Moving…" : "Move"}
        </button>
      </div>
    </form>
  );

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200">
      <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Location inventory</h3>
          <p className="text-sm text-gray-600 mt-1">
            Total stock {quantityText(stock)}. Move quantity between warehouse locations without
            changing that total.
          </p>
        </div>
        {locations.length > 0 && (
          <button
            type="button"
            onClick={() => openMove()}
            className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700"
          >
            Move
          </button>
        )}
      </div>
      <div className="p-6">
        {error && !moveOpen && (
          <p className="mb-4 rounded-lg bg-red-50 p-3 text-sm font-medium text-red-700">{error}</p>
        )}
        {loading ? (
          <p className="text-sm text-gray-500">Loading inventory…</p>
        ) : locations.length === 0 ? (
          <p className="text-sm text-gray-500">
            No location stock yet — receive this product first.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Warehouse
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Location
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                    Quantity
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {locations.map((row) => (
                  <tr key={row.warehouse_location_id}>
                    <td className="px-4 py-3 text-sm text-gray-900">{row.warehouse_name || "—"}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{row.location_name || "—"}</td>
                    <td className="px-4 py-3 text-right font-mono text-sm tabular-nums">
                      {quantityText(row.quantity)}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        type="button"
                        onClick={() => openMove(row.warehouse_location_id)}
                        className="rounded-md border px-3 py-1 text-sm font-semibold"
                      >
                        Move
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <EmptyModal
        open={moveOpen}
        setOpen={handleMoveModal}
        childComponent={moveOpen ? moveForm : null}
      />
    </div>
  );
}
